import React, {useCallback, useEffect, useRef} from "react";
import AppModal from "../../core/components/app-modal";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {animated} from "@react-spring/web";
import {useModalAnimation} from "../../core/animations";
import AppButton from "@common/components/app-button";
import AiButton from "./ui/ai-button";
import {useArticleApi} from "../../core/hooks/use-article-api";
import {useArticleForm} from "../hooks/use-article-form.hook";
import {useLocation, useNavigate} from "react-router-dom";
import {classNames} from "@utils/class-names";
import FileList from "./ui/file-list";
import FilePickerActions from "./ui/file-picker-actions";
import {ConvertDocumentAiModalData} from "../models/types/convert-document-ai-modal.types";
import {AiErrorResponseModalData} from "./ai-error-response-modal";
import {useDeferredSuccess} from "@common/hooks/use-deffered-success";
import {useOnPageReload} from "@common/hooks/use-on-page-reload.hook";
import {useDocumentConversion} from "../hooks/use-document-conversion.hook";
import {usePreventNavigation} from "@common/hooks/use-prevent-navigation.hook";
import {useOutsideClick} from "@common/hooks/use-outisde-click.hook";
import {clearConvertAiModalState, readConvertAiModalState} from "../helpers/convert-ai-modal-state";
import {
    ACCEPTED_FILE_TYPES,
    PROCESSING_BACK_PROMPT,
    PROCESSING_LEAVE_PROMPT
} from "../constants/convert-document-ai-modal";
import {useAiSourceDocuments} from "../hooks/use-ai-source-documents.hook";

const ARTICLE_FORM_ROUTE = "/app/articles/new";

const ConvertDocumentAiModal: React.FC = () => {
    const {hideModal, isOpenModal} = useModalWithData<ConvertDocumentAiModalData | null>(
        ModalIds.ARTICLE_CONVERT_WITH_AI
    );
    const {showModal: showModalAiError} = useModalWithData<AiErrorResponseModalData>(
        ModalIds.ARTICLE_AI_ERROR_RESPONSE
    );
    const modalOpen = isOpenModal();
    const {springProps, closeModal} = useModalAnimation(!!modalOpen, "medium");

    const containerRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const hasHydrated = useRef(false);

    const navigate = useNavigate();
    const location = useLocation();
    const {generateArticleFromDocument, error: apiError} = useArticleApi();
    const {startNewArticleFromAi} = useArticleForm();

    const selection = useAiSourceDocuments();
    const deferUntilVisible = useDeferredSuccess();

    const resetInput = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, []);

    const handleSuccess = useCallback((draft: unknown) => {
        selection.reset();
        resetInput();
        closeModal(() => hideModal());
        if (location.pathname !== ARTICLE_FORM_ROUTE) {
            navigate(ARTICLE_FORM_ROUTE);
        }
        startNewArticleFromAi(draft as Parameters<typeof startNewArticleFromAi>[0]);
    }, [selection, resetInput, closeModal, hideModal, location.pathname, navigate, startNewArticleFromAi]);

    const {isProcessing, convert} = useDocumentConversion({
        generateArticleFromDocument,
        onSuccess: handleSuccess,
        deferUntilVisible
    });

    const handleClose = useCallback((options?: { force?: boolean }) => {
        if (isProcessing && !options?.force) {
            return;
        }
        closeModal(() => {
            selection.clearFiles();
            hideModal();
        });
    }, [isProcessing, closeModal, selection, hideModal]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        selection.addFiles(Array.from(event.target.files ?? []));
        resetInput();
    };

    const handleConvert = async () => {
        if (selection.files.length === 0) {
            selection.setError("Wybierz co najmniej jeden plik, aby kontynuować.");
            return;
        }
        await convert(selection.files, selection.filesMeta);
    };

    useOnPageReload(clearConvertAiModalState);
    usePreventNavigation(isProcessing, {
        leavePrompt: PROCESSING_LEAVE_PROMPT,
        backPrompt: PROCESSING_BACK_PROMPT
    });
    useOutsideClick(containerRef, handleClose, !!modalOpen && !isProcessing);

    useEffect(() => {
        if (!modalOpen) {
            hasHydrated.current = false;
            selection.reset();
            resetInput();
            return;
        }
        if (hasHydrated.current) {
            return;
        }
        const persisted = readConvertAiModalState();
        if (persisted) {
            selection.hydrateMeta(persisted.files ?? []);
        }
        hasHydrated.current = true;
    }, [modalOpen, selection, resetInput]);

    useEffect(() => {
        if (apiError) {
            handleClose({force: true});
            showModalAiError({message: apiError, title: "Nie udało się wygenerować artykułu"});
        }
    }, [apiError]);

    return (
        <AppModal
            id={ModalIds.ARTICLE_CONVERT_WITH_AI}
            key={ModalIds.ARTICLE_CONVERT_WITH_AI}
            zIndex={50}
            forceFixed={true}
            classModal="!w-full !max-w-[640px]"
        >
            <animated.div
                style={springProps}
                className="w-full bg-white text-on-surface rounded-[32px] border border-outline-mild shadow-lvl-2 overflow-y-auto max-h-[75svh]"
                ref={containerRef}
            >
                <div className="px-8 pt-8 pb-6 space-y-6">
                    <h3 className="text-lg font-semibold leading-snug bg-gradient-to-r from-[#1B59B6] to-[#CB00C3] text-transparent bg-clip-text">
                        Utwórz artykuł z dokumentów za pomocą AI
                    </h3>
                    <p className="text-base text-on-surface leading-5">
                        Wybierz pliki, które mają stać się podstawą Twojego artykułu. AI poprawi dokumenty, upraszczając
                        język, aby był bardziej zrozumiały dla pacjenta. Tekst zostanie podzielony na sekcje z
                        odniesieniami, a Ty będziesz mógł go edytować, aby dostosować do swoich potrzeb.
                    </p>

                    <div>
                        <FileList
                            files={selection.filesMeta}
                            totalSize={selection.totalSize}
                            disabled={isProcessing}
                            onRemove={selection.removeFile}
                        />

                        <FilePickerActions
                            hasFiles={selection.filesMeta.length > 0}
                            disabled={isProcessing}
                            canAddMore={!selection.reachedFileLimit}
                            onPick={() => fileInputRef.current?.click()}
                            onClearAll={selection.clearFiles}
                        />

                        {selection.error && (
                            <p className="text-sm text-error mt-3">{selection.error}</p>
                        )}
                    </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-3 px-8 py-5 bg-white">
                    <AppButton
                        onClick={() => handleClose()}
                        disabled={isProcessing}
                        className={classNames(
                            "h-[40px] !bg-transparent !border-on-surface border-2 font-semibold !text-on-surface !rounded-full !px-6",
                            isProcessing ? "!opacity-40 cursor-not-allowed" : "hover:!bg-on-surface/5"
                        )}
                    >
                        Anuluj
                    </AppButton>
                    <AiButton
                        className="h-[40px] w-full justify-center md:w-fit"
                        loading={isProcessing}
                        label={isProcessing ? "Przetwarzanie..." : "Konwertuj za pomocą AI"}
                        disabled={isProcessing || selection.files.length === 0}
                        onClick={handleConvert}
                    />
                </div>

                <input
                    type="file"
                    accept={ACCEPTED_FILE_TYPES}
                    multiple
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileChange}
                />
            </animated.div>
        </AppModal>
    );
};

export default ConvertDocumentAiModal;
